import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import {
  DropdownItem,
  Order,
  OrderComponent,
} from "models/order-management/models";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { UtilityService } from "providers/utility-service/utility-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";
@Component({
  selector: "radio-group",
  templateUrl: "radio-group.html",
})
export class RadioGroupComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    const componentField = this.orderComponent.properties[0].name;
    const shouldClear = OrderFormState.shouldClear(
      componentField,
      this._orderFormState,
      orderFormState
    );
    const isSameProvider = this._orderFormState
      ? this._orderFormState.isSameProvider
      : undefined;
    this._orderFormState = orderFormState;
    if (
      this.orderComponent.properties.some(
        (property) => property.name === "approvalType"
      ) &&
      isProfessionalProvider(this._orderFormState.credentialedResource) &&
      isSameProvider !== orderFormState.isSameProvider
    ) {
      this.approvalTypeHidden = this._orderFormState.isSameProvider;
      this.checkApprovalTypeLogic();
      this.checkIsValid();
    }
    if (shouldClear && !!this.selectedOption) {
      this.selectedOption = undefined;
      const componentField = this.orderComponent.properties[0].name;
      this.orderCreateRequestEvent.emit({
        deletes: [componentField as keyof Order],
      });
      this.orderFormStateEvent.emit({
        approvalType: null,
      });
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  selectedOption: string;
  approvalTypeHidden: boolean = false;
  options: DropdownItem[] = [];
  _orderFormState: OrderFormState;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    public utilityService: UtilityService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.options = this.orderComponent.properties[0].dropdownItems;
    this.setDefault();
    this.checkApprovalTypeLogic();
    this.checkIsValid();
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  get isHidden() {
    return this.approvalTypeHidden;
  }

  public get shouldShow() {
    return !this._orderFormState.declined;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setDefault() {
    const defaultDropdown = this.options.find((option) => option.isDefault);
    if (defaultDropdown) {
      this.selectedOption = defaultDropdown.value;
      this.onRadioValueChange(this.selectedOption);
    }
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onRadioValueChange(event: string | "true" | "false" | undefined) {
    if (!event) {
      return;
    }

    const updateObject: OrderCreateRequestUpdate = { updates: {} };
    const componentField = this.orderComponent.properties[0].name;

    let updateValue: boolean | string;
    if (event == "true" || event == "false") {
      updateValue = event == "true" ? true : false;
    } else {
      updateValue = event;
      this.onRadioValueChangeString(event);
    }
    updateObject.updates[componentField] = updateValue;

    // Update create request
    this.orderCreateRequestEvent.emit(updateObject);

    // Update form state for certain fields
    if (["hospicePays"].includes(componentField)) {
      this.orderFormStateEvent.emit({
        [componentField]: updateValue,
      });
    }
    this.checkIsValid();
  }

  onRadioValueChangeString(event: string) {
    if (!event || event === this._orderFormState.approvalType) {
      return;
    }
    const componentField: string = this.orderComponent.properties[0].name;
    if (componentField === "approvalType") {
      this.orderFormStateEvent.emit({
        approvalType: event as Order.ApprovalTypeEnum,
      });
    }
  }

  checkIsValid() {
    if (
      this.orderComponent.properties.some(
        (property) => property.name === "approvalType"
      )
    ) {
      if (this._orderFormState.isSameProvider) {
        this.isValidEvent.emit(true);
      } else {
        if (!this.selectedOption || this.selectedOption.length === 0) {
          if (this._orderFormState.declined) {
            this.isValidEvent.emit(true);
          } else {
            this.isValidEvent.emit(false);
          }
        } else {
          this.isValidEvent.emit(true);
        }
      }
      return;
    }

    if (
      this.orderComponent.properties[0].required &&
      (!this.selectedOption || this.selectedOption.length === 0)
    ) {
      if (this._orderFormState.declined) {
        this.isValidEvent.emit(true);
      } else {
        this.isValidEvent.emit(false);
      }
    } else {
      this.isValidEvent.emit(true);
    }
  }

  isDisabled() {
    return (
      !this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    );
  }

  checkApprovalTypeLogic() {
    const componentField: string = this.orderComponent.properties[0].name;
    // Providers default to "Written" approval type
    if (
      componentField === "approvalType" &&
      isProfessionalProvider(this._orderFormState.credentialedResource) &&
      this._orderFormState.isSameProvider
    ) {
      this.selectedOption = Order.ApprovalTypeEnum.Written;
      this.onRadioValueChange(this.selectedOption);
    } else if (componentField === "approvalType") {
      this.selectedOption = "";
      this.onRadioValueChange(this.selectedOption);
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/radio-group/radio-group.ts